import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { getPostPublishChanges } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// "Chỉnh sửa sau công bố": các ô TKB của bản này đã bị sửa (dạy thay, đổi môn, xếp thêm, để trống...)
// SAU khi công bố — gom theo lớp, kèm GVCN để báo lại cho GVCN từng lớp.
const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const tietLabel = (p) => `${p < 5 ? "Sáng" : "Chiều"} t${(p % 5) + 1}`;
const cellText = (c) => (c ? `${c.subject}${c.teacher ? ` – ${c.teacher}` : " (trống GV)"}` : "— (không có tiết)");
const fmtAt = (ts) => (ts ? new Date(ts).toLocaleString("vi-VN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "");

const HappySchoolPostPublishChanges = ({ runId }) => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [classFilter, setClassFilter] = useState("");
  useEffect(() => {
    if (!schoolId || !runId) return;
    setData(null); setError("");
    getPostPublishChanges(schoolId, runId).then(setData).catch((e) => setError(e.message));
  }, [schoolId, runId]);
  const groups = useMemo(() => {
    const m = new Map();
    ((data && data.changes) || []).forEach((c) => {
      if (!m.has(c.className)) m.set(c.className, { className: c.className, gvcn: c.gvcn, items: [] });
      m.get(c.className).items.push(c);
    });
    return [...m.values()];
  }, [data]);
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return <p>Đang tải...</p>;
  const shown = classFilter ? groups.filter((g) => g.className === classFilter) : groups;
  const exportExcel = () => {
    const rows = [["Lớp", "GVCN", "Ngày", "Tiết", "Trước (lúc công bố)", "Sau (hiện tại)", "Ghi chú", "Lý do", "Lúc sửa"]];
    shown.forEach((g) => g.items.forEach((c) => rows.push([g.className, g.gvcn, DAYS[c.day], tietLabel(c.period), cellText(c.before), cellText(c.after), c.note, c.reason, fmtAt(c.at)])));
    const ws = XLSX.utils.aoa_to_sheet(rows); ws["!cols"] = [8, 22, 8, 9, 34, 34, 40, 24, 14].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Sửa sau công bố");
    XLSX.writeFile(wb, "TKB_chinh_sua_sau_cong_bo.xlsx");
  };
  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Các tiết của bản này đã bị sửa <b>sau khi công bố</b> (dạy thay, đổi môn, xếp thêm, để trống GV…), so với TKB lúc công bố — gom theo lớp kèm GVCN.
        {data.reconstructed && " Bản này công bố trước khi có tính năng lưu ảnh chụp lúc công bố, nên danh sách dựng lại từ nhật ký dạy thay (các sửa đổi không qua màn dạy thay có thể không hiện)."}
        {!data.published && " (Bản này hiện KHÔNG phải bản đang công bố.)"}
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 13 }}>Lớp:</span>
        <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #d1d5db" }}>
          <option value="">Tất cả ({groups.length} lớp · {data.changes.length} tiết)</option>
          {groups.map((g) => <option key={g.className} value={g.className}>{g.className} ({g.items.length})</option>)}
        </select>
        <button type="button" onClick={exportExcel} disabled={!shown.length} style={{ padding: "5px 12px", border: "1px solid #0f766e", borderRadius: 6, background: "#fff", color: "#0f766e", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>⬇️ Xuất Excel</button>
      </div>
      {!shown.length ? <p style={{ color: "#6b7280" }}>Chưa có chỉnh sửa nào sau khi công bố.</p> : shown.map((g) => (
        <div key={g.className} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <b>Lớp {g.className}</b>
            <span style={{ fontSize: 13 }}>GVCN: <b>{g.gvcn || "—"}</b> · {g.items.length} tiết sửa</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
              <thead><tr>{["Ngày", "Tiết", "Trước (lúc công bố)", "Sau (hiện tại)", "Ghi chú", "Lúc sửa"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>{g.items.map((c, i) => (
                <tr key={i}>
                  <td style={td}>{DAYS[c.day]}</td><td style={td}>{tietLabel(c.period)}</td>
                  <td style={{ ...td, color: "#6b7280", textDecoration: "line-through" }}>{cellText(c.before)}</td>
                  <td style={{ ...td, fontWeight: 600, color: "#047857" }}>{cellText(c.after)}</td>
                  <td style={{ ...td, color: "#92400e" }}>{c.note}{c.reason ? ` (Lý do: ${c.reason})` : ""}</td>
                  <td style={td}>{fmtAt(c.at)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f3f4f6", textAlign: "left", whiteSpace: "nowrap" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", verticalAlign: "top" };
export default HappySchoolPostPublishChanges;
